/**
 * AuthCallback route — handles all Supabase auth-link returns.
 *
 * Three flows arrive here:
 *
 *   1. Magic-link sign-in (Login.tsx → signInWithOtp)
 *      - Hash has access_token; SDK's detectSessionInUrl establishes
 *        the session.
 *      - Routing:
 *          no Contact            → /  (orphan view: "wait for invitation")
 *          Contact, isOrchist    → /
 *          Contact, pending invite → /acknowledge
 *
 *   2. Email change confirmation (EmailManager → auth.updateUser)
 *      - URL hash contains `type=email_change` (and tokens, after the
 *        new address has been attested in non-secure mode, OR after
 *        BOTH addresses have attested in secure mode).
 *      - Redundancy fix (JL 2026-04-26 eve): the user clicked the link
 *        from their inbox — that IS the gesture of attestation. They
 *        should not be dumped on the generic /login form. We render
 *        a contemplative confirmation surface instead.
 *      - Routing:
 *          signed in   → / with the swap reflected on next refresh
 *          not signed in → contemplative "Enter with the new anchor"
 *
 *   3. Edge cases (expired link, error, direct visit to /auth/callback)
 *      - Show loading for ~10s, then route to /login.
 */

import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface HashAnalysis {
  isEmailChange: boolean;
  isAuthCallback: boolean;
  errorDescription: string | null;
}

function analyzeAuthFragment(): HashAnalysis {
  if (typeof window === 'undefined') {
    return { isEmailChange: false, isAuthCallback: false, errorDescription: null };
  }
  const hash = window.location.hash || '';
  const search = window.location.search || '';
  const blob = hash + ' ' + search;
  // Our own intent marker survives Supabase's redirect verbatim. Any
  // Supabase-flavored fragment (type=email_change, tokens in hash) is a
  // secondary signal — but `intent=email-change` is the contract.
  const isEmailChange =
    blob.includes('intent=email-change') ||
    blob.includes('type=email_change') ||
    blob.includes('email_change_token') ||
    blob.includes('email_change=');
  const isAuthCallback =
    blob.includes('access_token') ||
    blob.includes('refresh_token') ||
    blob.includes('intent=') ||
    blob.includes('type=') ||
    blob.includes('error_description');
  let errorDescription: string | null = null;
  const m = blob.match(/error_description=([^&\s]+)/);
  if (m) {
    try {
      errorDescription = decodeURIComponent(m[1].replace(/\+/g, ' '));
    } catch {
      errorDescription = m[1];
    }
  }
  return { isEmailChange, isAuthCallback, errorDescription };
}

export default function AuthCallback() {
  const { loading, session, contact, contactChecked } = useAuth();
  const navigate = useNavigate();

  // Compute once at mount — the URL fragment doesn't change after this
  const [analysis] = useState<HashAnalysis>(analyzeAuthFragment);

  // Fallback: only bounce to /login if the URL has NO auth signal AND we
  // never get a session. Email-change flows (and any URL that looks like
  // an auth callback) get their own contemplative resolution below — no
  // automatic redirect to a "Send me the link" form, ever.
  useEffect(() => {
    if (analysis.isEmailChange || analysis.isAuthCallback) return;
    const timeout = setTimeout(() => {
      if (!session) {
        navigate('/login', { replace: true });
      }
    }, 10_000);
    return () => clearTimeout(timeout);
  }, [session, navigate, analysis.isEmailChange, analysis.isAuthCallback]);

  // Loading: show contextual copy
  if (loading || (session && !contactChecked) || (analysis.isAuthCallback && !session && !analysis.errorDescription)) {
    return (
      <Shell>
        <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">The Line</p>
        <h1 className="display text-3xl md:text-4xl font-medium italic text-ink/70">
          {analysis.isEmailChange ? 'Attesting the new anchor...' : 'Recognizing you...'}
        </h1>
        {analysis.isEmailChange && (
          <p className="text-sm text-ink/55 italic font-display max-w-md mx-auto leading-relaxed">
            The Line is recording the swap. A breath, then home.
          </p>
        )}
      </Shell>
    );
  }

  // Email change — contemplative surface, no /login bounce
  if (analysis.isEmailChange) {
    return <EmailChangeConfirmed signedIn={!!session} errorDescription={analysis.errorDescription} />;
  }

  // Magic-link sign-in completed — route by Contact state
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (contact && !contact.isOrchist && contact.invitationSentAt) {
    return <Navigate to="/acknowledge" replace />;
  }

  return <Navigate to="/" replace />;
}

// ---------------------------------------------------------------------------
// Email change confirmation surface
// ---------------------------------------------------------------------------

function EmailChangeConfirmed({
  signedIn,
  errorDescription,
}: {
  signedIn: boolean;
  errorDescription: string | null;
}) {
  // If signed in, the swap completed in this browser. Auto-redirect home
  // after a beat so the celebration is felt but the friction is zero.
  const navigate = useNavigate();
  useEffect(() => {
    if (!signedIn) return;
    const t = setTimeout(() => navigate('/', { replace: true }), 2200);
    return () => clearTimeout(t);
  }, [signedIn, navigate]);

  if (errorDescription) {
    return (
      <Shell>
        <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">The Line</p>
        <h1 className="display text-3xl md:text-4xl font-medium leading-tight">
          <span className="italic text-accent">The attestation faltered.</span>
        </h1>
        <p className="text-sm text-ink/55 italic font-display max-w-md mx-auto leading-relaxed">
          {errorDescription}
        </p>
        <p className="text-sm text-ink/55 italic font-display max-w-md mx-auto leading-relaxed">
          The link may have expired, or the swap was already completed.
          Return home and try again from Connections.
        </p>
        <div className="pt-4">
          <Link
            to="/"
            className="inline-block px-6 py-3 border border-ink/30 text-ink/80 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors"
          >
            Return to The Line
          </Link>
        </div>
      </Shell>
    );
  }

  if (signedIn) {
    // The swap landed in this browser — Supabase has issued a session for
    // the new email. AuthContext's USER_UPDATED listener has already
    // called sync_email_after_auth_change to mirror it into ContactEmail.
    return (
      <Shell>
        <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
          The new connection holds
        </p>
        <h1 className="display text-4xl md:text-5xl font-medium leading-[1.05]">
          <span className="italic text-accent">Anchor</span>
          <br />
          attested.
        </h1>
        <p className="text-lg text-ink/70 leading-relaxed max-w-md mx-auto font-display italic">
          The Line now recognizes you by the new address. The body of
          memory holds the swap.
        </p>
        <p className="text-xs text-ink/40 italic font-display pt-2">
          Returning home...
        </p>
      </Shell>
    );
  }

  // Not signed in — the click happened in a fresh browser, OR only one
  // side of a Secure-email-change has attested so far. Either way: don't
  // bounce to /login. Affirm what happened and let them step in.
  return (
    <Shell>
      <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
        The attestation is recorded
      </p>
      <h1 className="display text-4xl md:text-5xl font-medium leading-[1.05]">
        <span className="italic text-accent">A confirmation</span>
        <br />
        has been received.
      </h1>
      <p className="text-lg text-ink/70 leading-relaxed max-w-md mx-auto font-display italic">
        If both sides of the swap have attested (or only one was needed),
        the new address now anchors your record. Enter The Line with it.
      </p>
      <p className="text-sm text-ink/55 italic font-display max-w-md mx-auto leading-relaxed">
        If the other side hasn't attested yet, look for a confirmation
        in the previous anchor's inbox too. The swap completes when the
        last click lands.
      </p>
      <div className="pt-4 flex items-center justify-center gap-6 text-sm text-ink/60">
        <Link
          to="/login"
          className="underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60"
        >
          Enter the Line
        </Link>
        <span className="text-ink/30">&middot;</span>
        <a
          href="https://orchist.org"
          className="underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60"
        >
          Return to the Manifesto
        </a>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen flex flex-col justify-center items-center px-6 py-24">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 30% 20%, #8b7ab8 0%, transparent 55%), radial-gradient(circle at 70% 80%, #c9a961 0%, transparent 55%)',
        }}
      />
      <div className="container max-w-2xl text-center space-y-8 relative">{children}</div>
    </main>
  );
}
